import { useState, useEffect } from "react";
import { setExtra } from "@sentry/react";
import useLocalStorage from "react-use/lib/useLocalStorage";

import { getRegistrationInfo } from "api/getLinkInfo";
import { saveRegistrationData } from "api/saveRegistrationData";
import { client } from "api/client";
import { get } from "api/get";

import { StateManagerType } from "./StateManagerType";
import { Screen } from "./allScreens";

import { Patient } from "types/Patient";
import { Allergen } from "types/Allergen";
import { Dietanamnez } from "types/Dietanamnez";
import { UserType } from "types/UserType";
import { LinkInfo } from "types/LinkInfo";
import { Element } from "types/Data";
import { DietanamnezChartItem, DietanamnezItem, DietanamnezItemForm } from "types/DietanamnezItem";
import { Data, DataLite, Supplement, SupplementComposition } from "types/Data";
import { getPublicToken } from "api/getPublicToken";
import { generatePDFFromTemplate } from "utils/generatePDFFromTemplate";
import { AxiosRequestConfig } from "axios";
import { transformElementsListForLite } from "utils/tranformElementsListForLite";
import { insertCss } from "utils/insertCss";
import { hybrid } from "api/hybrid";
import { sendStartButtonMetric } from "api/sendBIMetrics";
import {deactivateSelfRegistrationLink} from "../../api/deactivateSelfRegistrationLink";

export const getDietanamnezData = (data: Data) => {
  const chemistry = (
    data.dietanamnez!.fields.custom_профиль_потребления_веществ || []
  ).reduce(
    (acc, item) => ({
      ...acc,
      [item.элемент_состава.replace("cXaX", "c105a902")]: item.среднее_за_день,
    }),
    {} as { [key: string]: number }
  );
  const items = data.elements;

  const groups = items.filter((item) => item?.___parent === null);

  const parseItems = (
    level: number,
    elements: any
  ): DietanamnezChartItem[] => {
    return elements
      .sort((a: any, b: any) => a.custom_для_сортировки - b.custom_для_сортировки)
      .map((item: any) => {
        const subitems = items.filter((elem) => {
          return (
            elem.___parent?.key === item.key
          );
        });
        const parsedSubitems = parseItems(level + 1, subitems);
        const norm = data.patient.fields.custom_коридор_спк!.find(
          (norm: any) => norm.элемент.replace("cXaX", "c105a902") === item.key
        );

        return {
          key: item.key,
          data: chemistry[item.key] || 0,
          element: item.key,
          label: item.custom_наименование,
          size: item.custom_ед_изм?.label,
          subitems: parsedSubitems,
          normMin: norm?.от,
          normMax: norm?.до === "Infinity" ? Infinity : norm?.до,
          sources: item.custom_источники,
          influence: item.custom_влияние,
        };
      });
  };

  return parseItems(1, groups);
};

async function parseSelftestData(values: any, token: AxiosRequestConfig) {
  const ration = {
    fields: values.ration.fields,
    related: {
      list: values.ration.related.list.map((relatedItem: any) => ({
        id: relatedItem.id,
        label: relatedItem.label,
        list: relatedItem.list,
      })),
    }
  }

  const rationItems = ration.related.list
    .find((obj: any) => obj.label === "Бад элемент рациона").list
    .map((item: any) => ({
      ...item.fields
    }));

  const elements = await client.get<{
    list: {
      key: string;
      fields: Omit<Element, "key">;
    }[];
  }>(`/c105a902t28r/json/v2`, {
    headers: {
      view: `c105a902ts5c28cs9r349`,
      token,
      limit: 10000,
    },
  });

  const supplements = await client.get<{
    list: {
      key: string;
      fields: Omit<Supplement, "key">;
    }[];
  }>(`/c105a902t157r/json/v2`, {
    headers: {
      view: `c105a902ts5c157cs9r572`,
      token,
      limit: 10000,
    },
  });
  const supplementsComposition = await client.get<{
    list: {
      key: string;
      fields: Omit<SupplementComposition, "key">;
    }[];
  }>(`/c105a902t161r/json/v2`, {
    headers: {
      view: `c105a902ts5c161cs9r575`,
      token,
      limit: 10000,
    },
  });

  const data = {
    ...values,
    ration,
    rationItems,
    elements: transformElementsListForLite(
      elements.data.list.map((item: any) => ({
        key: item.key,
        ...item.fields,
      }))
    ),
    supplements: supplements.data.list.map((item: any) => ({
      key: item.key,
      ...item.fields,
    })),
    supplementsComposition: supplementsComposition.data.list.map(
      (item: any) => ({
        key: item.key,
        ...item.fields,
      })
    ),
  };

  return data;
}

async function printSelf(
  values: any,
  logos?: [{ logo: string, logo_size?: string }],
  copyright?: string,
  extraHtml?: string | null,
  appointment?: string | null,
  isSuplementVisible?: boolean
) {
  const elements = await get(28, 349);
  // @ts-ignore
  const module = await import("@nutrilogic/print");

  const template = await module.runPrint(
    { ...values, elements },
    logos,
    copyright,
    extraHtml || "",
    appointment || "",
    {
       isSuplementVisiable: isSuplementVisible
    }
  );
  const isHybridUser = window.location.pathname === "/pl" || window.location.pathname === "/lp";

  if (isHybridUser) {
    await hybrid.post(
      `/${process.env.REACT_APP_API_KEY}t162r/json/v2`,
      {
        data: {
          custom_email: values.patient.fields.custom_login,
          custom_patientkey: values.patient.key,
          custom_исходник: template,
          custom_фио_пациента: values.patient.fields.custom_ио
        },
      },
      {
        responseType: "blob",
      }
    );

    return;
  }

  await client.post(
    `/${process.env.REACT_APP_API_KEY}t162r/json/v2`,
    {
      data: {
        custom_email: values.patient.fields.custom_login,
        custom_patientkey: values.patient.key,
        custom_исходник: template,
        custom_фио_пациента: values.patient.fields.custom_ио
      },
    },
    {
      responseType: "blob",
    }
  );

  return template;
}

async function printLiteDepersonalized(
  data: Data,
  token: AxiosRequestConfig,
  extraHtml?: string,
  setTemplate?: (template: string) => void
) {
  // @ts-ignore
  const module = await import("@nutrilogic/print");
  const parsedData = await parseSelftestData(data, token) as DataLite;
  const template = await module.runLiteDepersonalizedPrint({ data: parsedData, extraHtml});
  template && setTemplate && setTemplate(template)
  const blob = await generatePDFFromTemplate(template, token, "295.3mm");

  return blob;
}

export const useRegistartion = (
  token: string | null,
  userType: UserType,
  setIsLoading: (isLoading: boolean) => void,
  setSelftestData: (selftestData: Data) => void,
  setDietanamnezChartItems: (data: DietanamnezChartItem[]) => void,
  setBlob: (blob: Blob) => void,
  setParsedSelftestData: (data: DataLite) => void,
  isInitial: boolean,
  setTemplate: (template: string | null) => void
): StateManagerType => {
  const [screen, setScreen, removeScreen] = useLocalStorage<Screen>(
    `screen_${token}`
  );
  const [schemeData, setSchemeData, removeSchemeData] = useLocalStorage<Screen>(
    `schemeData_${token}`
  );

  const [isRegistrationDone, setIsRegistrationDone] = useLocalStorage<boolean>(`isRegistrationDone_${token}`, false);
  const [isError, setIsError] = useState<boolean>(false);
  const [linkInfo, setLinkInfo] = useState<LinkInfo>();

  const handleStart = async (token: string) => {
    try {
      // @ts-ignore
      const data = schemeData && schemeData?.custom?.custom_генерировать_бад_рацион_при_саморегистрации && !schemeData?.custom?.custom_не_запоминать_текущий_экран ? schemeData : await getRegistrationInfo(token);
      // @ts-ignore
      setSchemeData(data)

      if (!screen) {
        setScreen("WELCOME");
      }
      // @ts-ignore
      setLinkInfo(data);
    } catch (error) {
      console.error(error);
      setIsError(true);
    }
  };

  const isEpharmacy = linkInfo?.custom?.custom_генерировать_бад_рацион_при_саморегистрации;
  const notUseStorage = linkInfo?.custom?.custom_не_запоминать_текущий_экран;
  const showDeficit = linkInfo?.custom?.custom_показывать_экран_выявлен_дефицит_потребления;
  const showEmailButton =  linkInfo?.custom?.custom_показывать_кнопку_полный_отчет_на_почту;
  const showPhoneAtLastScreen = linkInfo?.custom?.custom_показывать_экран_с_вводом_телефона_для_записи_в_имя;

  if (screen === "FINISH" || (isEpharmacy && notUseStorage)) {
    window.addEventListener("beforeunload", () => {
      localStorage.clear();
      insertCss(linkInfo?.custom?.custom_additional_data?.css || "");
    })
  }

  const isShowAllergensScreen = linkInfo?.custom?.custom_показывать_экран_аллергии;

  let screens = [
    "WELCOME",
    "REGISTRATION_SUCCESS",
    "DIETANAMNEZ",
    "DIETANAMNEZ_TILES",
    "ALLERGENS",
    "AGREEMENT",
    "REGISTRATION",
    "ANTHROPOMETRY",
    "FINISH",
  ] as Screen[];

  if (linkInfo?.custom && !isShowAllergensScreen) {
    screens = [
    "WELCOME",
    "REGISTRATION_SUCCESS",
    "DIETANAMNEZ",
    "DIETANAMNEZ_TILES",
    "AGREEMENT",
    "REGISTRATION",
    "ANTHROPOMETRY",
    "FINISH",
    ];
  }

  const getScreensEpharmacy = () => {
    const defaultScreens = [
      "WELCOME",
      "REGISTRATION_SUCCESS",
      "DIETANAMNEZ",
      "DIETANAMNEZ_TILES",
      "PHONE_AS_FIO_SCREEN",
      "REGISTRATION",
      "DEFICIT",
      "PRODUCT",
      "EMAIL_SCREEN"
    ] as Screen[]

    if(!showDeficit) defaultScreens.splice(defaultScreens.indexOf('DEFICIT'), 1)
    if((screen === 'DEFICIT' || screen === 'PRODUCT' || screen === 'EMAIL_SCREEN') && isInitial && !notUseStorage) setScreen('REGISTRATION')
    if(!showEmailButton) screens.splice(screens.indexOf("EMAIL_SCREEN"), 1)
    if(!showPhoneAtLastScreen) screens.splice(screens.indexOf("PHONE_AS_FIO_SCREEN"), 1)

    return defaultScreens
  }

  if (isEpharmacy) { 
    screens = getScreensEpharmacy()
  }

  const PatientQueryParams = new URLSearchParams(document.location.search);

  const [patient, setPatient, removePatient] = useLocalStorage<Patient>(
    `patient_${token}`,
    {
      custom_firstname: PatientQueryParams.get('firstname') || "",
      custom_midname: PatientQueryParams.get('middlename') || "",
      custom_lastname: PatientQueryParams.get('lastname') || "",
      custom_дата_рождения: "",
      custom_sexint_save: "",
      custom_login:  PatientQueryParams.get('email') || "",
      custom_телефон: PatientQueryParams.get('phone') ? `+${PatientQueryParams.get('phone')?.trim()}` : "",
      custom_адрес: "",
    }
  );
  const [allergens = [], setAllergens, removeAllergens] = useLocalStorage<
    Allergen[]
  >(`allergens_${token}`, []);
  const [dietanamnez, setDietanamnez, removeDietanamnez] =
    useLocalStorage<Dietanamnez>(`dietanamnez_${token}`, {
      custom_специфические_виды_питания: [],
    });
  const [registrationData, setRegistrationData, removeRegistrationData] = useLocalStorage<any>(`registration_values_${token}`, null)
  const [dietanamnezItems = [], setDietanamnezItems, removeDietanamnezItems] =
    useLocalStorage<DietanamnezItem[]>(`dietanamnezItems_${token}`, []);
  const [step = 0, setStep, removeStep] = useLocalStorage<number>(
    `step_${token}`,
    0
  );

  useEffect(() => {
    setExtra("STATE", {
      screen,
      step,
      patient,
      allergens,
      dietanamnez,
      dietanamnezItems,
    });
  });

  useEffect(() => {
    if (!linkInfo && token) {
      handleStart(token);
    }
  }, [token]);
  
  const onPrevScreen = () => {
    const currentIndex = screens.indexOf(screen as typeof screens[number]);
    setScreen(screens[currentIndex - 1]);
  };

  const onNextScreen = async (exists = false) => {
    const currentIndex = screens.indexOf(screen as typeof screens[number]);
    const currentSavedIndex = screens.indexOf(screen as typeof screens[number]);
    const nextScreen = screens[currentIndex + 1];

    if (screen === "WELCOME" && token) await sendStartButtonMetric(token);

    if (nextScreen === "FINISH" && !exists) {
      setIsLoading(true);

      const values = await saveRegistrationData(setRegistrationData);
      await deactivateSelfRegistrationLink();
      const logos = linkInfo?.custom?.custom_additional_data?.report_title_page_logo;
      const copyright = linkInfo?.custom?.custom_additional_data?.report_footer_copyright;
      const extraHtml = linkInfo?.custom?.custom_дополнительный_текст_отчета;
      const appointment = linkInfo?.custom?.custom_формированть_в_да_назначение_к_врачам
        ? values.dietanamnez.fields.custom_заключение_направление_к_врачам
        : null;

      const isHybridUser = window.location.pathname === "/pl" || window.location.pathname === "/lp";

      if (userType === UserType.PRO || isHybridUser) {
        await printSelf(
          { ...values },
          logos,
          copyright,
          extraHtml,
          appointment,
        );
      }

      setIsLoading(false);
    }
    if ((nextScreen === "DEFICIT" && isEpharmacy) || (nextScreen === "PRODUCT" && isEpharmacy && !showDeficit)) {
      setIsLoading(true);

      const reportType = linkInfo?.custom?.custom_additional_data?.selfregistration_report_type;
      const token = await getPublicToken(client);

      /*Получение и сохранение данных для экрана дефицита и продукции. Есть на self pro и self lite*/
      // @ts-ignore
      const values = !notUseStorage && registrationData ? registrationData : await saveRegistrationData(setRegistrationData);
      await deactivateSelfRegistrationLink();
      setSelftestData(values);
      const parsedValues = await parseSelftestData(values, token);
      setParsedSelftestData(parsedValues);

      const dietanamnezData = getDietanamnezData(parsedValues);

      setDietanamnezChartItems(dietanamnezData);

      let blob = null;

      /*Дополнительная страница для отчёта  self pro и self lite*/
      const extraHtml = linkInfo?.custom?.custom_дополнительный_текст_отчета;

      if(reportType === 'pro') {
        const logos = linkInfo?.custom?.custom_additional_data?.report_title_page_logo;
        const copyright = linkInfo?.custom?.custom_additional_data?.report_footer_copyright;
        const appointment = linkInfo?.custom?.custom_формированть_в_да_назначение_к_врачам
          ? values.dietanamnez.fields.custom_заключение_направление_к_врачам
          : null;
        const isSuplementVisible =  linkInfo?.custom?.custom_additional_data?.report_is_supliment_visible || false
          const template = await printSelf(
            isSuplementVisible ? {...parsedValues} : {...values},
            logos,
            copyright,
            extraHtml,
            appointment,
            isSuplementVisible
          );
          template && setTemplate(template)
          // @ts-ignore
          blob = await generatePDFFromTemplate(template, token, "297mm");
        }
      else {
        blob = await printLiteDepersonalized(values, token, extraHtml || "", setTemplate)
    }

      setBlob(blob);

      window.parent.postMessage(
        JSON.stringify({ selftest_status: "COMPLETE", key: values.patient.key }),
        "*"
      );

      setIsLoading(false);
    }

    if (currentIndex >= currentSavedIndex && !isError) {
      setScreen(nextScreen);
      window.scrollTo(0, 0);
    }

    if (nextScreen === "FINISH" || nextScreen === "DEFICIT") {
      localStorage.removeItem('self_registration_id');
      localStorage.removeItem(`self_registration_id_${token}`);
    }

    if (nextScreen === "FINISH" || (isEpharmacy && notUseStorage)) {
      window.addEventListener("unload", () => {
        removeSchemeData();
        removeScreen();
        removeAllergens();
        removeDietanamnez();
        removeDietanamnezItems();
        removePatient();
        removeStep();
        // removeRegistrationData(); Скрыто, чтобы на последнем экране с отправкой отчёта не почту не делать лишний запрос сохранения пациента
      });
    }
  };

  return {
    linkInfo,
    progress: screens.indexOf(screen as typeof screens[number]) / (screens.length - 1),

    screen,
    setScreen,
    patient,
    updatePatient: (values: Partial<Patient>) => {
      setPatient({
        ...patient,
        ...values,
      });
    },
    allergens,
    setAllergens,
    dietanamnez,
    updateDietanamnez: async (values: Partial<Dietanamnez>) => {
      removeDietanamnezItems();

      setDietanamnez({
        ...dietanamnez!,
        ...values,
      });
    },
    dietanamnezItems,
    setDietanamnezItem: async (key: string, item?: DietanamnezItemForm) => {
      const withoutCurrent = dietanamnezItems.filter(
        (item) => item.custom_блюдо !== key
      );

      if (item) {
        setDietanamnezItems(
          withoutCurrent.concat({
            custom_добавить_исключить: 1,
            custom_блюдо: key,
            ...item,
          })
        );
      } else {
        /*Если прошный юзер - отправляем информацию о плитках "никогда/очень редко"*/
        if(userType === UserType.PRO) {
          setDietanamnezItems(
            withoutCurrent.concat({
              custom_добавить_исключить: 2,
              custom_блюдо: key
            })
          );
        }
        else {
          setDietanamnezItems(withoutCurrent);
        }
      }
    },
    step,
    setStep,

    onPrevScreen,
    onNextScreen,
    isError,
    registrationData,
    isRegistrationDone,
    setIsRegistrationDone,
  };
};
